import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  usePaginationState,
  useStatesWithFilterCounter,
  useInventoryQuery,
  clearSavedFilters,
} from '../../hooks';
import { DashboardHeader } from '../dashboard';
import {
  Button,
  Chip,
  Pagination,
  SimpleTable,
  TableWrapperFooter,
  Typography,
  TableDots,
  TableIconCell,
  inventoryDeleteModal,
  inventoryUpdateModal,
  inventoryCompleteModal,
} from '../../design-system';
import { i18n } from '../../i18n';
import { useStore } from '../../context';
import { showToRoles } from '../../utils';
import { TableCell } from '@mui/material';
import {
  InventoryStatusToChipColors,
  InventoryStatusToChipLabels,
  TABLE,
  inventoryTableDotsActions,
} from '../../const';
import { Filters } from './Filters';

interface InventoryPageProps {
  innerScroll?: boolean;
}

export const InventoryPage = styled.div<InventoryPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const InventoryHeader = ({ totalCount }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.inventory.title', { totalCount })}
      </Typography>
      {!showToRoles(['YARD_COMPANY_VIEWER'], user) && (
        <Button size={'medium'} to={'/dashboard/inventory/add'}>
          {i18n.t('button.addInventory')}
        </Button>
      )}
    </DashboardHeader>
  );
};

export const InventoryBody = ({ setTotalCount }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    yardName: '',
    yardId: '',
  });

  const {
    data = { data: [], totalCount: 0 },
    isFetching,
    refetch,
  } = useInventoryQuery({
    ...filteredStates,
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
  }, [isFetching]);

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleOnPageChange = (e: any, page: number) => {
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = (inventory: any, val: any) => {
    if (val === 'complete') {
      inventoryCompleteModal.id = inventory.id;
      inventoryCompleteModal.yardName = inventory.yardName;
      inventoryCompleteModal.yardId = inventory.yardId;

      inventoryCompleteModal.submitClick = () => {
        refetch();
      };
      inventoryCompleteModal.open();
    }
    if (val === 'edit') {
      inventoryUpdateModal.id = inventory.id;
      inventoryUpdateModal.yardName = inventory.yardName;
      inventoryUpdateModal.yardId = inventory.yardId;
      inventoryUpdateModal.assignedTo = inventory.assignedTo || [];

      inventoryUpdateModal.submitClick = () => {
        refetch();
      };
      inventoryUpdateModal.open();
    }
    if (val === 'delete') {
      inventoryDeleteModal.id = inventory.id;
      inventoryDeleteModal.yardName = inventory.yardName;
      inventoryDeleteModal.yardId = inventory.yardId;

      inventoryDeleteModal.submitClick = () => {
        refetch();
      };
      inventoryDeleteModal.open();
    }
  };

  const handleRowClick = (to: string) => {
    if (openDotsMenuIndex !== null) return;

    clearSavedFilters();
    navigate(to);
  };

  return (
    <SimpleTable
      data={data}
      filters={
        showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_VIEWER'], user)
          ? () => <Filters setStates={handleFilterChange} {...states} />
          : null
      }
      tableRowProps={(inventory, i) => ({
        key: inventory.id + i,
        onClick: () => handleRowClick(`/dashboard/inventory/${inventory.id}`),
      })}
      columns={[
        {
          column: 1,
          isHidden: () => showToRoles(['YARD_COMPANY_VIEWER'], user),
          headText: () => i18n.t('label.actions'),
          body: (inventory, i) => (
            <TableIconCell component={'th'} align={'left'}>
              <TableDots
                open={openDotsMenuIndex === i}
                onToggle={() => handleDotsToggle(i)}
                options={
                  inventory.status === 'IN_PROGRESS'
                    ? inventoryTableDotsActions
                    : [{ label: i18n.t('dropdown.delete'), value: 'delete' }]
                }
                onSelect={(val: any) => {
                  handleDotsOptionSelect(inventory, val);
                }}
              />
            </TableIconCell>
          ),
        },
        {
          headText: () => i18n.t('common.yard'),
          bodyText: (inventory) => inventory.yardName,
        },
        { headText: () => i18n.t('label.total'), bodyText: (inventory) => inventory.total },
        { headText: () => i18n.t('label.notFound'), bodyText: (inventory) => inventory.notFound },
        {
          headText: () => i18n.t('label.extraFound'),
          bodyText: (inventory) => inventory.extraFound,
        },
        { headText: () => i18n.t('label.found'), bodyText: (inventory) => inventory.found },
        {
          headText: () => i18n.t('label.progress'),
          bodyText: (inventory) => inventory.progress + '%',
        },
        {
          headText: () => i18n.t('label.status'),
          body: (inventory) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={InventoryStatusToChipLabels[inventory.status]}
                color={InventoryStatusToChipColors[inventory.status]}
              />
            </TableCell>
          ),
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handleOnPageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
